import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../apiClient';
import type { CustomerProfile, CustomerProfilePatch } from '../profileApi';
import {
  extractApiUser,
  normalizeProfileImage,
  pickProfileImageRaw,
} from '../../utils/profileImageUtils';

interface AuthState {
  user: CustomerProfile | null;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  loading: false,
  error: null,
};

function mapApiUserToAuthUser(
  apiUser: Record<string, unknown>,
  extras?: { token?: string },
): CustomerProfile {
  const baseURL = apiClient.defaults.baseURL || '';
  const rawImage = pickProfileImageRaw(apiUser);
  const profileImage = rawImage ? normalizeProfileImage(rawImage, baseURL) : undefined;
  return {
    id: String(apiUser.id ?? apiUser.user_id ?? ''),
    email: String(apiUser.email ?? ''),
    name: String(apiUser.name ?? apiUser.username ?? ''),
    role: String(
      (apiUser.role as { role_name?: string } | undefined)?.role_name ??
        apiUser.role_name ??
        apiUser.role ??
        'user',
    ).toLowerCase(),
    profile_image: profileImage,
    profileImage,
    avatar: profileImage,
    phoneNumber: String(apiUser.phoneNumber ?? apiUser.phone ?? ''),
    dob: apiUser.dob != null ? String(apiUser.dob) : undefined,
    address: apiUser.address != null ? String(apiUser.address) : undefined,
    createdAt: apiUser.createdAt != null ? String(apiUser.createdAt) : undefined,
    token: extras?.token ?? String(apiUser.token ?? ''),
  };
}

export const fetchUserThunk = createAsyncThunk(
  'auth/fetchUser',
  async (id: string, { rejectWithValue, getState }) => {
    try {
      const response = await apiClient.get(`/users/${id}`);
      const apiUser = extractApiUser(response.data);
      if (!apiUser) return rejectWithValue('User not found');
      const currentUser = (getState() as { auth: AuthState }).auth.user;
      const storedToken = localStorage.getItem('access_token') || '';
      return mapApiUserToAuthUser(apiUser, {
        token: currentUser?.token || storedToken,
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch user');
    }
  },
);

export const updateUserThunk = createAsyncThunk(
  'auth/updateUser',
  async (payload: CustomerProfilePatch, { rejectWithValue, getState }) => {
    try {
      const { id, ...fields } = payload;
      const response = await apiClient.patch(`/users/${id}`, fields);
      const apiUser = extractApiUser(response.data);
      const currentUser = (getState() as { auth: AuthState }).auth.user;
      const baseURL = apiClient.defaults.baseURL || '';
      const rawImage =
        pickProfileImageRaw(apiUser ?? undefined) ??
        fields.profile_image ??
        currentUser?.profile_image ??
        currentUser?.profileImage;
      const profileImage = rawImage
        ? normalizeProfileImage(rawImage, baseURL) ?? rawImage
        : undefined;
      const storedToken = localStorage.getItem('access_token') || '';
      const mapped = apiUser ? mapApiUserToAuthUser(apiUser, { token: currentUser?.token || storedToken }) : null;
      return {
        id: String(mapped?.id ?? apiUser?.id ?? id),
        email: String(mapped?.email ?? fields.email ?? currentUser?.email ?? ''),
        name: String(mapped?.name ?? fields.name ?? currentUser?.name ?? ''),
        role: mapped?.role ?? currentUser?.role ?? 'user',
        profile_image: profileImage,
        profileImage,
        avatar: profileImage ?? currentUser?.avatar,
        phoneNumber: String(mapped?.phoneNumber ?? currentUser?.phoneNumber ?? ''),
        dob: mapped?.dob ?? fields.dob ?? currentUser?.dob,
        address: mapped?.address ?? fields.address ?? currentUser?.address,
        createdAt: mapped?.createdAt ?? currentUser?.createdAt,
        token: String(mapped?.token ?? currentUser?.token ?? storedToken),
      } satisfies CustomerProfile;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(err.response?.data?.message || 'Failed to update user');
    }
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<AuthState['user']>) => {
      state.user = action.payload;
      state.loading = false;
      state.error = null;
    },
    clearUser: (state) => {
      state.user = null;
      state.loading = false;
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserThunk.fulfilled, (state, action) => {
        state.user = state.user ? { ...state.user, ...action.payload } : action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchUserThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateUserThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserThunk.fulfilled, (state, action) => {
        state.user = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(updateUserThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setUser, clearUser, setLoading, setError } = authSlice.actions;
export const selectCurrentUser = (state: { auth: AuthState }) => state.auth.user;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.loading;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;
export default authSlice.reducer;
